import React from 'react';
import { isTextParam } from '../utils/parameterEvaluator';

// ── Design tokens — 3 acentos: marca, alerta, neutro ───────────────────────
const C = {
  primary:  '#1A3A5C',   // marca / encabezados
  teal:     '#1A3A5C',   // (alias marca — antes verde-azulado, ahora unificado)
  alert:    '#C62828',   // valores fuera de rango
  neutral:  '#5B6B7B',   // estado normal / texto atenuado
  muted:    '#5B6B7B',   // (alias de neutral)

  bgCard:   '#F5F7FA',
  bgHead:   '#EDF1F5',   // fondo suave para encabezados de sección
  bgAlert:  '#FCEBEA',
  obsBg:    '#FFFDF2',
  obsYellow:'#FFFDF2',
  text:     '#1F2937',
  border:   '#E2E8F0',
  white:    '#FFFFFF',
};

const FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const W    = 794;   // A4 at 96 dpi
const PH   = 40;    // horizontal padding (px)

// Estado de un parámetro con color unificado: normal/neutro vs alerta única.
function displayStatus(p) {
  const s = p.status;
  if (s === 'text' || isTextParam(p)) {
    return { label: '—', color: C.neutral, bg: C.bgCard, abnormal: false };
  }
  if (s === 'normal') return { label: 'Normal', color: C.neutral, bg: C.bgCard, abnormal: false };
  if (s === 'unknown' || s === 'indeterminate') {
    return { label: s === 'unknown' ? 'Sin ref.' : 'N/D', color: C.neutral, bg: C.bgCard, abnormal: false };
  }
  if (s === 'positive') return { label: 'Positivo +', color: C.alert, bg: C.bgAlert, abnormal: true };
  if (s === 'negative') return { label: 'Negativo −', color: '#16A34A', bg: '#DCFCE7', abnormal: false };
  // Fuera de rango: dirección según valor vs referencia, color de alerta único
  const v = parseFloat(String(p.value ?? '').replace(',', '.'));
  let up = s === 'high';
  if (!isNaN(v)) {
    if (p.refMax != null && v > parseFloat(p.refMax)) up = true;
    else if (p.refMin != null && v < parseFloat(p.refMin)) up = false;
  }
  const critical = s === 'critical';
  return {
    label: `${up ? 'Alto ▲' : 'Bajo ▼'}${critical ? ' !' : ''}`,
    color: C.alert, bg: C.bgAlert, abnormal: true, critical,
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────
const fmtNum = (v) => {
  if (v == null || v === '' || v === '-') return '—';
  return String(v).replace('.', ',');
};

const fmtRef = (mn, mx) =>
  mn != null && mx != null ? `${fmtNum(mn)} – ${fmtNum(mx)}` : '—';

const fmtFireTs = (v) => {
  if (!v) return new Date().toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const d = typeof v === 'object' && v.seconds ? new Date(v.seconds * 1000) : new Date(v);
  return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function calcAge(birthdate) {
  if (!birthdate) return null;
  const birth = new Date(birthdate);
  const now   = new Date();
  const ms    = now - birth;
  const years = Math.floor(ms / (365.25 * 864e5));
  if (years >= 1) return `${years} año${years > 1 ? 's' : ''}`;
  const months = Math.floor(ms / (30.44 * 864e5));
  if (months >= 1) return `${months} mes${months > 1 ? 'es' : ''}`;
  const days = Math.floor(ms / 864e5);
  return `${days} día${days !== 1 ? 's' : ''}`;
}

// ── Sub-components ─────────────────────────────────────────────────────────

// Encabezado de sección liviano: borde lateral de color + texto en negrita
function SectionHeader({ children, extra, radius }) {
  return (
    <div style={{
      borderLeft: `3px solid ${C.primary}`,
      background: C.bgHead,
      borderTopRightRadius: radius ? 4 : 0,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '3px 10px',
    }}>
      <span style={{ color: C.primary, fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
        {children}
      </span>
      {extra}
    </div>
  );
}

function ReportHeader({ order, result, branch }) {
  const examCat = (result?.examCategory || 'INFORME DE LABORATORIO').toUpperCase();
  const speciesLabel = (order?.patientSpecies || '').toUpperCase();
  const title = speciesLabel ? `${examCat} — ${speciesLabel}` : examCat;
  const emitted = fmtFireTs(result?.reportGeneratedAt || null);
  const muestra = result?.tipoMuestra || '';
  const emitido = ['reported', 'approved'].includes(result?.status);
  const b = branch || {};
  const contactLine = [b.address, b.phone, b.email].filter(Boolean).join(' · ');

  return (
    <>
      {/* Main header row — white background */}
      <div style={{ background: C.white, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `10px ${PH}px 8px` }}>

        {/* Left: logo + clinic identity */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img
            src={b.logoURL || '/logo-horizontal.png'}
            alt="Logo"
            style={{ height: 50, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
          />
          <div>
            <div style={{ color: C.primary, fontWeight: 700, fontSize: 14, letterSpacing: 0.2 }}>
              {b.name || 'Hospital Clínico Veterinario UDLA'}
            </div>
            <div style={{ color: C.muted, fontSize: 8.5, marginTop: 2 }}>
              Laboratorio Clínico Veterinario
            </div>
            <div style={{ color: C.muted, fontSize: 8, marginTop: 1 }}>
              {contactLine || 'Calle José Massoud Sarquis N°482 · Melipilla'}
            </div>
          </div>
        </div>

        {/* Right: order number + dates */}
        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div>
            <div style={{ color: C.muted, fontSize: 7.5, textTransform: 'uppercase', letterSpacing: 0.8 }}>N° Orden</div>
            <div style={{ color: C.primary, fontWeight: 700, fontSize: 14 }}>{order?.orderNumber || '—'}</div>
          </div>
          <div>
            <div style={{ color: C.muted, fontSize: 7.5, textTransform: 'uppercase', letterSpacing: 0.8 }}>Fecha emisión</div>
            <div style={{ color: C.text, fontWeight: 600, fontSize: 8.5 }}>{emitted}</div>
          </div>
        </div>
      </div>

      {/* Title + status bar — light background with brand bottom border */}
      <div style={{ background: C.bgCard, borderTop: `1px solid ${C.border}`, borderBottom: `2px solid ${C.primary}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `5px ${PH}px` }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div style={{ color: C.primary, fontWeight: 700, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            {title}
          </div>
          {muestra && (
            <div style={{ color: C.neutral, fontSize: 8.5, fontWeight: 600 }}>
              Muestra: <span style={{ color: C.text }}>{muestra}</span>
            </div>
          )}
        </div>
        <div style={{
          background: emitido ? C.bgCard : '#FFF8E1',
          color:      emitido ? C.primary : '#B45309',
          fontWeight: 700, fontSize: 8, textTransform: 'uppercase',
          padding: '3px 10px', borderRadius: 20,
          border: `1px solid ${emitido ? C.primary : '#D97706'}`,
        }}>
          {emitido ? '✓ EMITIDO' : '⏳ PENDIENTE'}
        </div>
      </div>
    </>
  );
}

function PatientCard({ patient, owner, vet, order }) {
  const name       = patient?.name       || order?.patientName    || '—';
  const species    = patient?.species    || order?.patientSpecies || '—';
  const breed      = patient?.breed      || '—';
  const sex        = patient?.genero     || patient?.sex        || '—';
  const age        = calcAge(patient?.birthDate || patient?.birthdate)                  || '—';
  const weight     = patient?.weight     ? `${patient.weight} kg` : '—';
  const ownerName  = owner?.name         || order?.ownerName      || '—';
  const ownerPhone = owner?.phone        || owner?.telefono       || '—';
  const vetName    = vet?.nombre
    ? `${vet.nombre}${vet.apellidos ? ' ' + vet.apellidos : ''}`
    : (order?.vetName || '—');

  const rows = [
    [{ label: 'Nombre',             val: name },      { label: 'Especie',   val: species }],
    [{ label: 'Propietario',        val: ownerName }, { label: 'Raza',      val: breed }],
    [{ label: 'Edad',               val: age },       { label: 'Sexo',      val: sex }],
    [{ label: 'Peso',               val: weight },    { label: 'N° Orden',  val: order?.orderNumber || '—' }],
    [{ label: 'Médico solicitante', val: vetName },   { label: 'Teléfono',  val: ownerPhone }],
  ];

  const cellStyle = (i) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'baseline',
    padding: '3px 12px',
    borderLeft: i > 0 ? `1px solid ${C.border}` : 'none',
    gap: 6,
  });

  return (
    <div style={{ margin: `8px ${PH}px 0`, border: `1px solid ${C.border}`, borderRadius: 4, overflow: 'hidden' }}>
      <SectionHeader>Identificación del Paciente</SectionHeader>
      {rows.map((row, ri) => (
        <div key={ri} style={{ display: 'flex', background: ri % 2 === 0 ? C.white : C.bgCard, borderBottom: ri < rows.length - 1 ? `1px solid ${C.border}` : 'none' }}>
          {row.map((cell, ci) => (
            <div key={ci} style={cellStyle(ci)}>
              <span style={{ fontSize: 7.5, fontWeight: 700, color: C.muted, minWidth: 110, flexShrink: 0 }}>
                {cell.label}:
              </span>
              <span style={{ fontSize: 9, color: C.text, fontWeight: 500 }}>{cell.val}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// Resumen compacto de alterados: una sola línea con chips (sin duplicar referencia/estado del detalle)
function AlteredSummary({ params }) {
  return (
    <div style={{ margin: `8px ${PH}px 0`, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.alert}`, borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ background: C.bgAlert, padding: '3px 10px' }}>
        <span style={{ color: C.alert, fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Parámetros alterados — {params.length} fuera de referencia
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 10px', background: C.white }}>
        {params.map((p, i) => {
          const st = displayStatus(p);
          return (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'baseline', gap: 4,
              border: `1px solid ${C.border}`, borderRadius: 3, padding: '2px 7px', fontSize: 8.5,
            }}>
              <span style={{ color: C.text, fontWeight: 600 }}>{p.paramName || p.name || '—'}</span>
              <span style={{ color: C.alert, fontWeight: 700 }}>{fmtNum(p.value)}{p.unit ? ` ${p.unit}` : ''}</span>
              <span style={{ color: C.neutral, fontSize: 7.5 }}>{st.label}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function NoAlterations() {
  return (
    <div style={{ margin: `8px ${PH}px 0`, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.primary}`, borderRadius: 4, background: C.bgCard, padding: '6px 12px' }}>
      <span style={{ color: C.primary, fontSize: 9, fontWeight: 600 }}>
        Todos los parámetros evaluados se encuentran dentro de los rangos de referencia.
      </span>
    </div>
  );
}

function ExamSection({ title, params }) {
  const thStyle = { padding: '3px 8px', textAlign: 'left', color: C.neutral, fontWeight: 700, fontSize: 8, borderBottom: `1px solid ${C.border}` };
  const hasMetodo = params.some((p) => p.metodo);

  return (
    <div style={{ margin: `8px ${PH}px 0` }}>
      <SectionHeader radius>{title}</SectionHeader>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 8.5, border: `1px solid ${C.border}`, borderTop: 'none', borderRadius: '0 0 4px 4px', overflow: 'hidden' }}>
        <thead>
          <tr style={{ background: C.bgCard }}>
            <th style={{ ...thStyle, width: hasMetodo ? '28%' : '38%' }}>Parámetro</th>
            <th style={{ ...thStyle, width: '12%', textAlign: 'center' }}>Resultado</th>
            <th style={{ ...thStyle, width: '10%', textAlign: 'center' }}>Unidad</th>
            {hasMetodo && <th style={{ ...thStyle, width: '18%' }}>Método</th>}
            <th style={{ ...thStyle, width: '16%', textAlign: 'center' }}>V. Referencia</th>
            <th style={{ ...thStyle, width: '16%', textAlign: 'center' }}>Estado</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p, i) => {
            const st = displayStatus(p);
            const isAbnormal = st.abnormal;
            const bg = i % 2 === 0 ? C.white : C.bgCard;
            const isText = isTextParam(p) || p.status === 'text';

            // Parámetro descriptivo (ej. Morfología): texto libre a todo el ancho
            if (isText) {
              return (
                <tr key={i} style={{ background: bg, borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '3px 8px', color: C.text }}>
                    {p.paramName || p.name || '—'}
                  </td>
                  <td colSpan={hasMetodo ? 5 : 4}
                    style={{ padding: '3px 8px', color: C.text, fontSize: 8.5, whiteSpace: 'pre-wrap' }}>
                    {String(p.value || '').trim() || '—'}
                  </td>
                </tr>
              );
            }

            return (
              <tr key={i} style={{ background: bg, borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '3px 8px', color: C.text, fontWeight: isAbnormal ? 600 : 400 }}>
                  {isAbnormal && (
                    <span style={{ color: C.alert, fontWeight: 800, marginRight: 4, fontSize: 9 }}>*</span>
                  )}
                  {p.paramName || p.name || '—'}
                </td>
                <td style={{ padding: '3px 8px', textAlign: 'center', color: isAbnormal ? C.alert : C.text, fontWeight: isAbnormal ? 700 : 500, fontSize: 9 }}>
                  {fmtNum(p.value)}
                </td>
                <td style={{ padding: '3px 8px', textAlign: 'center', color: C.neutral, fontSize: 8 }}>
                  {p.unit || '—'}
                </td>
                {hasMetodo && (
                  <td style={{ padding: '3px 8px', color: C.neutral, fontSize: 8 }}>
                    {p.metodo || '—'}
                  </td>
                )}
                <td style={{ padding: '3px 8px', textAlign: 'center', color: C.neutral, fontSize: 8 }}>
                  {p.refDisplay || fmtRef(p.refMin, p.refMax)}
                </td>
                <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                  <span style={{ display: 'inline-block', background: st.bg, color: st.color, fontWeight: 700, fontSize: 7.5, padding: '1px 7px', borderRadius: 3, whiteSpace: 'nowrap' }}>
                    {st.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ObservationsBox({ text, reviewer }) {
  return (
    <div style={{ margin: `8px ${PH}px 0`, background: C.obsBg, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.primary}`, borderRadius: 4, padding: '7px 12px' }}>
      <div style={{ fontSize: 7.5, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
        Observaciones del Técnico:
      </div>
      {text ? (
        <div style={{ fontSize: 9, color: C.text, lineHeight: 1.5 }}>{text}</div>
      ) : (
        <div style={{ fontSize: 8.5, color: C.neutral, fontStyle: 'italic' }}>Sin observaciones adicionales.</div>
      )}
    </div>
  );
}

function SignatureBlock({ title, name, subtitle }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 12px' }}>
      <div style={{ width: '80%', borderBottom: `1.5px solid ${C.primary}`, marginBottom: 5 }} />
      <div style={{ fontSize: 9, fontWeight: 700, color: C.primary, textAlign: 'center' }}>{name}</div>
      <div style={{ fontSize: 8, fontWeight: 700, color: C.neutral, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2, textAlign: 'center' }}>{title}</div>
      {subtitle && (
        <div style={{ fontSize: 7.5, color: C.neutral, marginTop: 1, textAlign: 'center' }}>{subtitle}</div>
      )}
    </div>
  );
}

function ReportFooter({ order, result, branch }) {
  const generatedAt = new Date().toLocaleString('es-CL');
  const validatorName = result?.reviewedByName || '___________________________';
  const b = branch || {};
  const footerLine = `${b.name || 'Hospital Clínico Veterinario UDLA'} · Laboratorio Clínico`;
  const contactLine = [b.website, b.email].filter(Boolean).join(' · ');

  return (
    <div style={{ margin: `12px ${PH}px 0`, paddingBottom: 12 }}>
      {/* Signature area */}
      <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 12, paddingTop: 24 }}>
        <SignatureBlock
          title="Director Técnico"
          name="Dr. Javier Aguilar Valdés"
          subtitle="Médico Veterinario"
        />
        <SignatureBlock
          title="T.M. Validador del Informe"
          name={validatorName}
          subtitle="Tecnólogo Médico"
        />
      </div>

      {/* Divider — solid brand color */}
      <div style={{ height: 1.5, background: C.primary, borderRadius: 1, marginBottom: 8 }} />

      {/* Validation line */}
      <div style={{ textAlign: 'center', fontSize: 8.5, color: C.primary, fontWeight: 700, marginBottom: 8, letterSpacing: 0.5 }}>
        Informe Validado por T.M. {validatorName}
      </div>

      {/* Disclaimer */}
      <div style={{ textAlign: 'center', fontSize: 7.5, color: C.neutral, fontStyle: 'italic', lineHeight: 1.5 }}>
        Este informe no constituye diagnóstico. Los resultados deben ser interpretados por el médico veterinario
        tratante en el contexto clínico del paciente. Consulte siempre a su médico veterinario.
      </div>

      {/* Bottom bar */}
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 7.5, color: C.neutral }}>
        <span>
          {footerLine}
          {contactLine && <> · <span style={{ color: C.primary }}>{contactLine}</span></>}
        </span>
        <span>Generado: {generatedAt}</span>
      </div>
    </div>
  );
}

function DraftWatermark() {
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 10 }}>
      <div style={{ transform: 'rotate(-38deg)', fontSize: 68, fontWeight: 900, color: 'rgba(220,38,38,0.07)', textTransform: 'uppercase', letterSpacing: 8, userSelect: 'none', whiteSpace: 'nowrap' }}>
        BORRADOR — NO VÁLIDO
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function LabReport({ result, order, patient, owner, vet, branch, mode = 'screen' }) {
  const params = (result?.extractedParameters || []).map(p => ({ ...p }));

  const altered = params.filter(p => ['high', 'low', 'critical'].includes(p.status));

  const categorized = params.reduce((acc, p) => {
    const cat = p.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const isDraft = !['reported', 'approved'].includes(result?.status);
  const hasAnyRef = params.some(p => p.refMin != null && p.refMax != null);

  return (
    <div
      id="lab-report-print"
      style={{
        width: W,
        background: C.white,
        fontFamily: FONT,
        color: C.text,
        position: 'relative',
        boxSizing: 'border-box',
        paddingBottom: 4,
      }}
    >
      {isDraft && mode !== 'print' && <DraftWatermark />}

      {mode !== 'print' && <ReportHeader order={order} result={result} branch={branch} />}
      <PatientCard  patient={patient} owner={owner} vet={vet} order={order} />

      {result?.examTypeName && (
        <div style={{ margin: `10px ${PH}px 0`, textAlign: 'center' }}>
          <span style={{ color: C.primary, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {result.examTypeName}
          </span>
        </div>
      )}

      {/* Altered summary — only when there are reference ranges */}
      {hasAnyRef && altered.length > 0  && <AlteredSummary params={altered} />}
      {hasAnyRef && altered.length === 0 && <NoAlterations />}

      {Object.entries(categorized).map(([cat, catParams]) => (
        <ExamSection key={cat} title={cat} params={catParams} />
      ))}

      <ObservationsBox text={result?.observations} reviewer={result?.reviewedByName} />
      <ReportFooter order={order} result={result} branch={branch} />
    </div>
  );
}
